import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useStyles, useColors } from "../ui";
import { useFieldEvent } from "../field-event-context";
import { encodeStatusPoll } from "../../../shared/src/proto-encode";
import { db } from "../db";
import { patients, cases } from "../../../shared/src/schema";
import { isNotNull, eq, and } from "drizzle-orm";
import { useCommand, useRpc } from "../store";
import { useAuth } from "../auth";
import { mapBridge } from "../map-bridge";
import { WebMap } from "../web-map";
import { uuidv7 } from "../../../shared/src/ids";
import { TRIAGE_CONFIG, triageLabel, triageSortOrder, triageColorHex } from "../triage";

// ─── Types ────────────────────────────────────────────────────────────

type TriageLevel = "T1" | "T2" | "T3" | "T4" | "T0" | null;

interface LiveSoldier {
  soldierId: string;
  name: string;
  watchId: string;
  triageLevel: TriageLevel;
  triageScore: number | null;
  confidence: number | null;
  hrBpm: number | null;
  spo2Pct: number | null;
  batteryPct: number | null;
  lastUpdate: number | null;
  lat: number | null;
  lon: number | null;
  caseId: string | null;  // set when a case is auto-created on first triage alert
}

interface Assignment {
  watchId: string;
  medicId: string;
  medicName: string | null;
  completedAt: number | null;
}

interface MedicInfo {
  id: string;
  name: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────

// TRIAGE_CONFIG, triageLabel, triageSortOrder, triageColorHex imported from ../triage

function triageSortKey(s: LiveSoldier): number {
  return triageSortOrder(s.triageLevel);
}

// null falls back to colors.text (theme-aware); T0 and named levels use static hex.
function triageColor(level: TriageLevel, colors: ReturnType<typeof useColors>): string {
  return triageColorHex(level, colors.text);
}

function formatAge(ms: number | null): string {
  if (ms === null) return "—";
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function triageRemarks(s: LiveSoldier): string {
  const parts: string[] = [];
  if (s.triageLevel) parts.push(triageLabel(s.triageLevel));
  if (s.hrBpm != null) parts.push(`HR ${s.hrBpm}`);
  if (s.spo2Pct != null) parts.push(`SpO₂ ${s.spo2Pct}%`);
  if (s.batteryPct != null) parts.push(`Bat ${s.batteryPct}%`);
  return parts.join(" · ");
}

const ASSIGNED_COLOR = "#f39c12"; // amber — distinct from all triage colors

// Map watch triage level (T1-T4) to case triage category (I-IV)
const TRIAGE_TO_CATEGORY: Record<string, string> = {
  T1: "I", T2: "II", T3: "III", T4: "IV", T0: "IV",
};

// ─── Bridge helpers ───────────────────────────────────────────────────

async function addCotMarker(watchId: string, name: string, lat: number | null, lon: number | null, remarks: string, triageLevel: string | null) {
  let finalLat = lat;
  let finalLon = lon;
  if (finalLat == null || finalLon == null) {
    const self = await mapBridge.getSelfLocation();
    finalLat = self?.lat ?? 0;
    finalLon = self?.lon ?? 0;
  }
  mapBridge.addMarker({ uid: watchId, name, lat: finalLat, lon: finalLon, remarks, triageLevel: triageLevel ?? "" });
}

function focusCot(watchId: string) {
  mapBridge.focusMarker(watchId);
}

// ─── Assign picker (host only) ────────────────────────────────────────

function AssignPicker({
  watchId,
  medics,
  onAssign,
  onClose,
}: {
  watchId: string;
  medics: MedicInfo[];
  onAssign: (watchId: string, medicId: string) => void;
  onClose: () => void;
}) {
  const colors = useColors();
  const styles = useStyles();

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: "100%",
        right: 0,
        zIndex: 300,
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: "6px",
        padding: "6px",
        minWidth: "160px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
      }}
    >
      <div style={{ fontSize: "0.7rem", color: colors.textMuted, padding: "2px 4px 6px" }}>
        Assign to medic:
      </div>
      {medics.length === 0 && (
        <div style={{ fontSize: "0.8rem", color: colors.textMuted, padding: "4px" }}>No medics available</div>
      )}
      {medics.map((m) => (
        <button
          key={m.id}
          style={{ ...styles.btn, display: "block", width: "100%", textAlign: "left", marginBottom: "2px" }}
          onClick={() => { onAssign(watchId, m.id); onClose(); }}
        >
          {m.name ?? m.id}
        </button>
      ))}
    </div>
  );
}

// ─── Soldier row ──────────────────────────────────────────────────────

function SoldierRow({
  soldier, onSelect, selected, assignment, isHost, medics, myUserId, fieldEventId, eventStopped,
  onAssign, onComplete, onCreateCase,
}: {
  soldier: LiveSoldier;
  onSelect: (s: LiveSoldier) => void;
  selected: boolean;
  assignment: Assignment | null;
  isHost: boolean;
  medics: MedicInfo[];
  myUserId: string | undefined;
  fieldEventId: string | null;
  eventStopped: boolean;
  onAssign: (watchId: string, medicId: string) => void;
  onComplete: (watchId: string) => void;
  onCreateCase: (soldier: LiveSoldier) => void;
}) {
  const colors = useColors();
  const styles = useStyles();
  const navigate = useNavigate();
  const [showAssign, setShowAssign] = useState(false);
  const color = triageColor(soldier.triageLevel, colors);
  const isAssigned = assignment !== null && assignment.completedAt === null;
  const isMyAssignment = isAssigned && assignment?.medicId === myUserId;
  const borderColor = isAssigned ? ASSIGNED_COLOR : color;

  return (
    <tr
      onClick={() => onSelect(soldier)}
      style={{
        cursor: "pointer",
        background: selected ? colors.bgAlt : isAssigned ? ASSIGNED_COLOR + "12" : undefined,
        borderLeft: `3px solid ${borderColor}`,
      }}
      onMouseOver={(e) => { if (!selected) e.currentTarget.style.background = colors.bgAlt; }}
      onMouseOut={(e) => { e.currentTarget.style.background = isAssigned ? ASSIGNED_COLOR + "12" : ""; }}
    >
      <td style={{ ...styles.td, color, fontWeight: 600, whiteSpace: "nowrap" }}>
        {triageLabel(soldier.triageLevel)}
        {soldier.triageScore !== null && (
          <span style={{ fontWeight: 400, color: colors.textMuted, marginLeft: "6px" }}>
            {soldier.triageScore.toFixed(1)}
          </span>
        )}
      </td>
      <td style={styles.td}>
        {soldier.name}
        {isAssigned && (
          <span style={{ marginLeft: "6px", fontSize: "0.7rem", color: ASSIGNED_COLOR, fontWeight: 600 }}>
            ● {assignment?.medicName ?? "Medic"}
          </span>
        )}
      </td>
      <td style={styles.td}>
        {soldier.confidence !== null ? (
          <span style={{ color: soldier.confidence < 50 ? "#e67e22" : colors.text }}>
            {soldier.confidence}%
          </span>
        ) : "—"}
      </td>
      <td style={styles.td}>{soldier.hrBpm ?? "—"}</td>
      <td style={styles.td}>{soldier.spo2Pct !== null ? `${soldier.spo2Pct}%` : "—"}</td>
      <td style={styles.td}>{soldier.batteryPct !== null ? `${soldier.batteryPct}%` : "—"}</td>
      <td style={{ ...styles.td, color: colors.textMuted, fontSize: "0.8rem" }}>
        {formatAge(soldier.lastUpdate)}
      </td>
      <td style={{ ...styles.td, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
        {isAssigned && !eventStopped && (
          <button
            style={{ ...styles.btn, background: "#27ae60", color: "#fff", border: "none", fontSize: "0.75rem" }}
            onClick={() => onComplete(soldier.watchId)}
          >
            Triage Complete
          </button>
        )}
        {!eventStopped && (
          soldier.caseId ? (
            <button
              style={{ ...styles.btn, fontSize: "0.75rem", marginLeft: "4px", background: "#2980b9", color: "#fff", border: "none" }}
              onClick={(e) => { e.stopPropagation(); navigate(`/cases/${soldier.caseId}`); }}
            >
              Go to Case
            </button>
          ) : (
            <button
              style={{ ...styles.btn, fontSize: "0.75rem", marginLeft: "4px" }}
              onClick={(e) => { e.stopPropagation(); onCreateCase(soldier); }}
              title="Create a medical case for this soldier"
            >
              Create Case
            </button>
          )
        )}
        {isHost && fieldEventId && !eventStopped && (
          <div style={{ position: "relative", display: "inline-block" }}>
            <button
              style={{ ...styles.btn, fontSize: "0.75rem", marginLeft: isMyAssignment || isAssigned ? "4px" : "0" }}
              onClick={() => setShowAssign((v) => !v)}
            >
              {isAssigned ? "Reassign" : "Assign"}
            </button>
            {showAssign && (
              <AssignPicker
                watchId={soldier.watchId}
                medics={medics}
                onAssign={onAssign}
                onClose={() => setShowAssign(false)}
              />
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Share picker ─────────────────────────────────────────────────────

function SharePicker({ fieldEventId, onClose, onInvite }: {
  fieldEventId: string;
  onClose: () => void;
  onInvite: (medic: MedicInfo) => void;
}) {
  const styles = useStyles();
  const colors = useColors();
  const rpc = useRpc();
  const dispatch = useCommand();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Array<{ id: string; name: string | null; email: string }>>([]);
  const [sending, setSending] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await rpc<{ users: Array<{ id: string; name: string | null; email: string }> }>({
          type: "searchUsers",
          search,
        });
        setResults(res.users.filter((u) => u.id !== user?.userId));
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [search, rpc, user]);

  const invite = async (invited: { id: string; name: string | null }) => {
    setSending(invited.id);
    try {
      await dispatch("fieldEvent.addMedic", { uuid: fieldEventId, medicUuid: invited.id });
      onInvite({ id: invited.id, name: invited.name });
    } finally {
      setSending(null);
    }
    onClose();
  };

  return (
    <div style={{
      position: "absolute", top: "100%", right: 0, zIndex: 200,
      background: colors.bg, border: `1px solid ${colors.border}`,
      borderRadius: "6px", padding: "8px", minWidth: "220px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    }}>
      <input
        autoFocus
        placeholder="Search by name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ ...styles.input, width: "100%", marginBottom: "6px" }}
      />
      {results.length === 0 && (
        <div style={{ fontSize: "0.8rem", color: colors.textMuted, padding: "4px 0" }}>No users found</div>
      )}
      {results.map((u) => (
        <div key={u.id} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 0" }}>
          <span style={{ flex: 1, fontSize: "0.875rem" }}>{u.name ?? u.email}</span>
          <button style={styles.btn} disabled={sending === u.id} onClick={() => invite(u)}>
            {sending === u.id ? "…" : "Invite"}
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Soldier table ────────────────────────────────────────────────────

function SoldierTable({
  soldiers, label, assignments, isHost, medics, myUserId, fieldEventId, eventStopped,
  selectedId, onSelect, onAssign, onComplete, onCreateCase,
}: {
  soldiers: LiveSoldier[];
  label?: string;
  assignments: Assignment[];
  isHost: boolean;
  medics: MedicInfo[];
  myUserId: string | undefined;
  fieldEventId: string | null;
  eventStopped: boolean;
  selectedId: string | null;
  onSelect: (s: LiveSoldier) => void;
  onAssign: (watchId: string, medicId: string) => void;
  onComplete: (watchId: string) => void;
  onCreateCase: (soldier: LiveSoldier) => void;
}) {
  const styles = useStyles();
  const colors = useColors();

  if (soldiers.length === 0) return null;

  return (
    <div>
      {label && (
        <div style={{
          padding: "4px 12px",
          fontSize: "0.7rem",
          fontWeight: 700,
          color: colors.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          background: colors.bgAlt,
          borderBottom: `1px solid ${colors.border}`,
        }}>
          {label}
        </div>
      )}
      <table style={{ ...styles.table, width: "100%" }}>
        <thead>
          <tr>
            <th style={styles.th}>Triage</th>
            <th style={styles.th}>Soldier</th>
            <th style={styles.th}>Confidence</th>
            <th style={styles.th}>HR</th>
            <th style={styles.th}>SpO₂</th>
            <th style={styles.th}>Battery</th>
            <th style={styles.th}>Updated</th>
            <th style={styles.th} />
          </tr>
        </thead>
        <tbody>
          {soldiers.map((s) => {
            const assignment = assignments.find((a) => a.watchId === s.watchId && a.completedAt === null) ?? null;
            return (
              <SoldierRow
                key={s.soldierId}
                soldier={s}
                selected={s.soldierId === selectedId}
                assignment={assignment}
                isHost={isHost}
                medics={medics}
                myUserId={myUserId}
                fieldEventId={fieldEventId}
                eventStopped={eventStopped}
                onSelect={onSelect}
                onAssign={onAssign}
                onComplete={onComplete}
                onCreateCase={onCreateCase}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────

export function LiveTriagePage() {
  const styles = useStyles();
  const colors = useColors();
  const { boardAddress, setBoardAddress, pairings, registerPairing, fieldEventId, resetFieldEvent } = useFieldEvent();
  const { user } = useAuth();
  const rpc = useRpc();
  const dispatch = useCommand();
  const navigate = useNavigate();

  const [soldiers, setSoldiers] = useState<LiveSoldier[]>([]);
  const soldiersRef = useRef(soldiers);
  useEffect(() => { soldiersRef.current = soldiers; }, [soldiers]);

  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [medics, setMedics] = useState<MedicInfo[]>([]);
  const [leadMedicId, setLeadMedicId] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastFeedUpdate, setLastFeedUpdate] = useState<number | null>(null);
  const [feedConnected, setFeedConnected] = useState(false);
  const [polling, setPolling] = useState(false);
  const pendingResponsesRef = useRef(0);
  const [showShare, setShowShare] = useState(false);
  const [eventStopped, setEventStopped] = useState(false);
  const [stopping, setStopping] = useState(false);

  const isHost = !!(user?.userId && (user.userId === leadMedicId || boardAddress));

  // ── Clear any map markers left over from past-events ──
  useEffect(() => { mapBridge.clearMarkers(); }, []);

  // ── Reset field event context when leaving after the event has ended ──
  const eventStoppedRef = useRef(false);
  useEffect(() => { eventStoppedRef.current = eventStopped; }, [eventStopped]);
  useEffect(() => {
    return () => { if (eventStoppedRef.current) resetFieldEvent(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Stop event (host) ──
  const handleStop = useCallback(async () => {
    if (!fieldEventId || stopping) return;
    setStopping(true);
    try {
      await dispatch("fieldEvent.stop", { uuid: fieldEventId });
    } finally {
      setStopping(false);
    }
    if (boardAddress) {
      (window as any).DataLab?.disconnectGatt?.(boardAddress); // BLE disconnect — ATAK direct
      setBoardAddress("");
    }
    mapBridge.clearMarkers();
    setEventStopped(true);
  }, [fieldEventId, boardAddress, stopping, dispatch, setBoardAddress]);

  // ── Viewer: listen for session-ended notification ──
  useEffect(() => {
    const handler = (e: Event) => {
      const { fieldEventId: stoppedId } = (e as CustomEvent).detail;
      if (stoppedId === fieldEventId) {
        mapBridge.clearMarkers();
        setEventStopped(true);
      }
    };
    window.addEventListener("biostream:field-event-stopped", handler);
    return () => window.removeEventListener("biostream:field-event-stopped", handler);
  }, [fieldEventId]);

  // ── Session data: history (viewer), assignments, and medics ──
  useEffect(() => {
    if (!fieldEventId) return;
    (async () => {
      try {
        const res = await rpc<{
          rows: Array<Record<string, any>>;
          assignments: Array<{ watchId: string; medicId: string; medicName: string | null; assignedAt: number; completedAt: number | null }>;
          medics: Array<{ id: string; name: string | null }>;
          leadMedicId: string | null;
        }>({ type: "watchEventLog", fieldEventId });

        setAssignments(res.assignments.map((a) => ({
          watchId: a.watchId,
          medicId: a.medicId,
          medicName: a.medicName,
          completedAt: a.completedAt,
        })));
        setMedics(res.medics);
        setLeadMedicId(res.leadMedicId);

        // History sync for viewers without BLE
        if (!boardAddress && res.rows.length > 0) {
          const soldierMap = new Map<string, LiveSoldier>();
          for (const row of res.rows) {
            const cmdPayload = typeof row.payload === "string" ? JSON.parse(row.payload) : (row.payload ?? {});
            const vitals = cmdPayload.payload ?? cmdPayload;
            const type = (row.type as string).toUpperCase();
            const watchId: string = row.watchId;
            if (!soldierMap.has(watchId)) {
              soldierMap.set(watchId, {
                soldierId: row.soldierId ?? watchId,
                name: row.soldierName ?? row.soldierId ?? watchId,
                watchId,
                triageLevel: null, triageScore: null, confidence: null,
                hrBpm: null, spo2Pct: null, batteryPct: null,
                lastUpdate: null, lat: null, lon: null, caseId: null,
              });
            }
            const entry = soldierMap.get(watchId)!;
            if (type === "TRIAGEALERT") {
              soldierMap.set(watchId, {
                ...entry,
                triageLevel: (vitals.triageLevel as TriageLevel) ?? null,
                triageScore: vitals.triageScore ?? null,
                confidence: vitals.confidence ?? null,
                hrBpm: vitals.hrBpm ?? null,
                spo2Pct: vitals.spo2Pct ?? null,
                batteryPct: vitals.batteryPct ?? null,
                lat: row.gpsLat ?? entry.lat, lon: row.gpsLon ?? entry.lon,
                lastUpdate: row.timestamp,
              });
            } else if (type === "MANDOWN") {
              soldierMap.set(watchId, {
                ...entry, triageLevel: "T1",
                lat: row.gpsLat ?? entry.lat, lon: row.gpsLon ?? entry.lon,
                lastUpdate: row.timestamp,
              });
            } else if (type === "STATUSRESPONSE") {
              soldierMap.set(watchId, {
                ...entry,
                hrBpm: vitals.hrBpm ?? entry.hrBpm,
                spo2Pct: vitals.spo2Pct ?? entry.spo2Pct,
                batteryPct: vitals.batteryPct ?? entry.batteryPct,
                lat: row.gpsLat ?? entry.lat, lon: row.gpsLon ?? entry.lon,
                lastUpdate: row.timestamp,
              });
            }
          }
          if (soldierMap.size > 0) {
            // Look up any cases already created for these soldiers in this field event
            try {
              const caseRows = await db
                .select({ id: cases.id, patientId: cases.patientId })
                .from(cases)
                .where(eq(cases.fieldEventId, fieldEventId));
              for (const c of caseRows) {
                for (const soldier of soldierMap.values()) {
                  if (soldier.soldierId === c.patientId) {
                    soldierMap.set(soldier.watchId, { ...soldier, caseId: c.id });
                  }
                }
              }
            } catch { /* local DB not ready */ }

            setSoldiers((prev) => {
              const next = [...prev];
              for (const soldier of soldierMap.values()) {
                const idx = next.findIndex((s) => s.watchId === soldier.watchId);
                if (idx >= 0) next[idx] = { ...next[idx], ...soldier };
                else next.push(soldier);
              }
              return next;
            });
            // Place map markers for all soldiers with known positions
            const assignmentMap = new Map(res.assignments.map((a) => [a.watchId, a]));
            for (const soldier of soldierMap.values()) {
              if (soldier.lat == null || soldier.lon == null) continue;
              const a = assignmentMap.get(soldier.watchId);
              const isAssigned = a && a.completedAt === null;
              addCotMarker(
                soldier.watchId, soldier.name, soldier.lat, soldier.lon,
                triageRemarks(soldier), isAssigned ? "ASSIGNED" : soldier.triageLevel,
              );
            }
          }
        }
      } catch { /* not yet accessible or offline */ }
    })();
  }, [fieldEventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load paired soldiers — context pairings first, then DB ──
  useEffect(() => {
    const snapshotPairings = pairings;
    if (snapshotPairings.size > 0) {
      setSoldiers(
        Array.from(snapshotPairings.values()).map((p) => ({
          soldierId: p.soldierId, name: p.soldierName, watchId: p.watchId,
          triageLevel: null, triageScore: null, confidence: null,
          hrBpm: null, spo2Pct: null, batteryPct: null,
          lastUpdate: null, lat: null, lon: null, caseId: null,
        }))
      );
    }
    (async () => {
      try {
        const rows = await db
          .select({ id: patients.id, name: patients.name, lastUsedWatch: patients.lastUsedWatch })
          .from(patients).where(isNotNull(patients.lastUsedWatch));
        setSoldiers((prev) => {
          const next = [...prev];
          for (const row of rows) {
            if (!row.lastUsedWatch) continue;
            if (next.some((s) => s.soldierId === row.id)) continue;
            next.push({
              soldierId: row.id, name: row.name ?? row.id, watchId: row.lastUsedWatch,
              triageLevel: null, triageScore: null, confidence: null,
              hrBpm: null, spo2Pct: null, batteryPct: null,
              lastUpdate: null, lat: null, lon: null, caseId: null,
            });
            registerPairing({ watchId: row.lastUsedWatch, soldierId: row.id, soldierName: row.name ?? row.id });
          }
          return next;
        });
      } catch { /* local DB not ready */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ensure the current user (host) is always in the medics list ──
  useEffect(() => {
    if (!user?.userId || !isHost) return;
    setMedics((prev) => {
      if (prev.some((m) => m.id === user.userId)) return prev;
      return [{ id: user.userId, name: user.name ?? null }, ...prev];
    });
  }, [user?.userId, user?.name, isHost]);

  // ── Assignment actions ──
  const handleAssign = useCallback(async (watchId: string, medicId: string) => {
    if (!fieldEventId) return;
    await dispatch("fieldEvent.assignTriage", { uuid: fieldEventId, watchId, medicUuid: medicId });
    const medicName = medics.find((m) => m.id === medicId)?.name ?? null;
    setAssignments((prev) => [
      ...prev.filter((a) => !(a.watchId === watchId && a.completedAt === null)),
      { watchId, medicId, medicName, completedAt: null },
    ]);
    // Place an "ASSIGNED" marker and focus on it
    const soldier = soldiersRef.current.find((s) => s.watchId === watchId);
    if (soldier) {
      addCotMarker(watchId, soldier.name, soldier.lat, soldier.lon, `Assigned to ${medicName ?? "medic"}`, "ASSIGNED");
    }
  }, [fieldEventId, dispatch, medics]);

  const handleComplete = useCallback(async (watchId: string) => {
    if (!fieldEventId) return;
    // Optimistic update first so the UI responds immediately
    setAssignments((prev) =>
      prev.map((a) => a.watchId === watchId && a.completedAt === null ? { ...a, completedAt: Date.now() } : a)
    );
    // Restore the soldier's actual triage color on the map
    const soldier = soldiersRef.current.find((s) => s.watchId === watchId);
    if (soldier) {
      addCotMarker(watchId, soldier.name, soldier.lat, soldier.lon, triageRemarks(soldier), soldier.triageLevel);
    }
    try {
      await dispatch("fieldEvent.triageComplete", { uuid: fieldEventId, watchId });
    } catch { /* offline — command queued, will sync */ }
  }, [fieldEventId, dispatch]);

  // ── Listen for incoming assignment events from server WS ──
  useEffect(() => {
    const handleAssigned = (e: Event) => {
      const { watchId, medicId, fieldEventId: fid } = (e as CustomEvent).detail;
      if (fid !== fieldEventId) return;
      const medicName = medics.find((m) => m.id === medicId)?.name ?? null;
      setAssignments((prev) => [
        ...prev.filter((a) => !(a.watchId === watchId && a.completedAt === null)),
        { watchId, medicId, medicName, completedAt: null },
      ]);
      // If this is MY assignment: highlight + focus on map
      if (medicId === user?.userId) {
        const soldier = soldiersRef.current.find((s) => s.watchId === watchId);
        if (soldier) {
          addCotMarker(watchId, soldier.name, soldier.lat, soldier.lon, `Assigned to me`, "ASSIGNED");
          mapBridge.focusMarker(watchId);
        }
      }
    };
    const handleComplete = (e: Event) => {
      const { watchId, fieldEventId: fid } = (e as CustomEvent).detail;
      if (fid !== fieldEventId) return;
      setAssignments((prev) =>
        prev.map((a) => a.watchId === watchId && a.completedAt === null ? { ...a, completedAt: Date.now() } : a)
      );
      const soldier = soldiersRef.current.find((s) => s.watchId === watchId);
      if (soldier) {
        addCotMarker(watchId, soldier.name, soldier.lat, soldier.lon, triageRemarks(soldier), soldier.triageLevel);
      }
    };
    window.addEventListener("biostream:triage-assigned", handleAssigned);
    window.addEventListener("biostream:triage-complete", handleComplete);
    return () => {
      window.removeEventListener("biostream:triage-assigned", handleAssigned);
      window.removeEventListener("biostream:triage-complete", handleComplete);
    };
  }, [fieldEventId, medics, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Create / navigate to case ──
  const handleCreateCase = useCallback((soldier: LiveSoldier) => {
    if (!soldier.soldierId || !fieldEventId) return;
    const caseUuid = uuidv7();
    // Update soldier state so the button flips to "Go to Case" immediately
    setSoldiers((prev) => prev.map((s) =>
      s.watchId === soldier.watchId ? { ...s, caseId: caseUuid } : s
    ));
    navigate(`/cases/${caseUuid}`);
    dispatch("case.create", { uuid: caseUuid, fieldEventId });
    dispatch("case.setPatient", { uuid: caseUuid, patientUuid: soldier.soldierId });
    dispatch("case.setEventTime", { uuid: caseUuid, timestamp: Date.now() });
    const triageCat = soldier.triageLevel ? TRIAGE_TO_CATEGORY[soldier.triageLevel] : null;
    if (triageCat) dispatch("case.setTriageCategory", { uuid: caseUuid, triageCategory: triageCat as any });
    if (user?.name) dispatch("case.setMedicName", { uuid: caseUuid, name: user.name });
  }, [fieldEventId, dispatch, navigate, user]);

  // ── Status poll ──
  const handleStatusPoll = useCallback(() => {
    if (!boardAddress || polling) return;
    const selected = soldiersRef.current.find((s) => s.soldierId === selectedId);
    const watchId = selected?.watchId ?? "";
    pendingResponsesRef.current = !watchId ? soldiersRef.current.length : 1;
    setPolling(true);
    (window as any).DataLab?.writeGatt?.(boardAddress, encodeStatusPoll(watchId)); // BLE write — ATAK direct
  }, [boardAddress, polling, selectedId]);

  // ── Live feed: biostream:watch-event from BleBridge (host) or WS (viewer) ──
  useEffect(() => {
    const handler = (e: Event) => {
      const { type, data } = (e as CustomEvent).detail as { type: string; data: Record<string, any> };
      setLastFeedUpdate(Date.now());
      setFeedConnected(true);

      if (type === "TRIAGE_ALERT") {
        setSoldiers((prev) => {
          const next = [...prev];
          let idx = next.findIndex((s) => s.watchId === data.watchId);
          if (idx < 0 && data.soldierId) {
            next.push({
              soldierId: data.soldierId, name: data.soldierName ?? data.soldierId,
              watchId: data.watchId, triageLevel: null, triageScore: null, confidence: null,
              hrBpm: null, spo2Pct: null, batteryPct: null, lastUpdate: null, lat: null, lon: null, caseId: null,
            });
            idx = next.length - 1;
          }
          if (idx < 0) return prev;
          const updated: LiveSoldier = {
            ...next[idx],
            triageLevel: (data.triageLevel as TriageLevel) ?? null,
            triageScore: data.triageScore ?? null, confidence: data.confidence ?? null,
            hrBpm: data.hrBpm ?? null, spo2Pct: data.spo2Pct ?? null, batteryPct: data.batteryPct ?? null,
            lat: data.lat ?? null, lon: data.lon ?? null, lastUpdate: Date.now(),
          };
          next[idx] = updated;
          // Use ASSIGNED color if currently assigned
          const isAssigned = assignments.find((a) => a.watchId === data.watchId && a.completedAt === null);
          addCotMarker(updated.watchId, updated.name, updated.lat, updated.lon,
            triageRemarks(updated), isAssigned ? "ASSIGNED" : updated.triageLevel);
          return next;
        });
      }

      if (type === "MAN_DOWN") {
        setSoldiers((prev) => {
          const next = [...prev];
          const idx = next.findIndex((s) => s.watchId === data.watchId);
          if (idx < 0) return prev;
          const updated: LiveSoldier = {
            ...next[idx], triageLevel: "T1", lastUpdate: Date.now(),
            lat: data.lat ?? next[idx].lat, lon: data.lon ?? next[idx].lon,
          };
          next[idx] = updated;
          addCotMarker(updated.watchId, updated.name, updated.lat, updated.lon,
            "MAN DOWN · " + triageRemarks(updated), "T1");
          return next;
        });
      }

      if (type === "STATUS_RESPONSE") {
        pendingResponsesRef.current = Math.max(0, pendingResponsesRef.current - 1);
        if (pendingResponsesRef.current === 0) setPolling(false);
        const lat = data.lat ?? null;
        const lon = data.lon ?? null;
        setSoldiers((prev) => {
          const next = [...prev];
          const idx = next.findIndex((s) => s.watchId === data.watchId);
          if (idx < 0) return prev;
          next[idx] = { ...next[idx], hrBpm: data.hrBpm ?? null, spo2Pct: data.spo2Pct ?? null,
            batteryPct: data.batteryPct ?? null, lat: lat ?? next[idx].lat, lon: lon ?? next[idx].lon, lastUpdate: Date.now() };
          return next;
        });
        const existing = soldiersRef.current.find((s) => s.watchId === data.watchId);
        if (existing) {
          const isAssigned = assignments.find((a) => a.watchId === data.watchId && a.completedAt === null);
          addCotMarker(existing.watchId, existing.name, lat ?? existing.lat, lon ?? existing.lon,
            triageRemarks({ ...existing, hrBpm: data.hrBpm ?? null, spo2Pct: data.spo2Pct ?? null, batteryPct: data.batteryPct ?? null }),
            isAssigned ? "ASSIGNED" : existing.triageLevel);
        }
      }
    };
    window.addEventListener("biostream:watch-event", handler);
    return () => window.removeEventListener("biostream:watch-event", handler);
  }, [assignments]); // re-bind when assignments change so ASSIGNED color is fresh

  const handleSelect = useCallback((soldier: LiveSoldier) => {
    setSelectedId(soldier.soldierId);
    focusCot(soldier.watchId);
  }, []);

  // Split soldiers into assigned (to me or all if host) and others.
  // All four are memoized — recomputed only when the underlying data changes, not every render.
  const activeAssignments = useMemo(
    () => assignments.filter((a) => a.completedAt === null),
    [assignments],
  );

  const sorted = useMemo(
    () => [...soldiers].sort((a, b) => {
      const diff = triageSortKey(a) - triageSortKey(b);
      return diff !== 0 ? diff : (b.triageScore ?? 0) - (a.triageScore ?? 0);
    }),
    [soldiers],
  );

  const myAssignedWatchIds = useMemo(
    () => new Set(
      activeAssignments
        .filter((a) => isHost ? true : a.medicId === user?.userId)
        .map((a) => a.watchId),
    ),
    [activeAssignments, isHost, user?.userId],
  );

  const assignedSoldiers = useMemo(
    () => sorted.filter((s) =>  myAssignedWatchIds.has(s.watchId)),
    [sorted, myAssignedWatchIds],
  );
  const otherSoldiers = useMemo(
    () => sorted.filter((s) => !myAssignedWatchIds.has(s.watchId)),
    [sorted, myAssignedWatchIds],
  );


  const sharedProps = {
    assignments: activeAssignments,
    isHost,
    medics,
    myUserId: user?.userId,
    fieldEventId,
    eventStopped,
    selectedId,
    onSelect: handleSelect,
    onAssign: handleAssign,
    onComplete: handleComplete,
    onCreateCase: handleCreateCase,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Toolbar */}
      <div style={{ ...styles.toolbar, gap: "12px", position: "relative" }}>
        <span style={{ fontWeight: 600 }}>Live Triage</span>
        <span style={{ fontSize: "0.75rem", color: feedConnected ? "#27ae60" : colors.textMuted }}>
          {feedConnected
            ? `Live · ${lastFeedUpdate ? formatAge(lastFeedUpdate) : ""}`
            : boardAddress ? "Waiting for feed…" : "Viewer mode"}
        </span>
        <span style={{ flex: 1 }} />
        {fieldEventId && (
          <div style={{ position: "relative" }}>
            <button style={styles.btn} onClick={() => setShowShare((v) => !v)}>Share</button>
            {showShare && (
              <SharePicker
                fieldEventId={fieldEventId}
                onClose={() => setShowShare(false)}
                onInvite={(m) => setMedics((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m])}
              />
            )}
          </div>
        )}
        {boardAddress && (
          <button style={styles.btn} disabled={polling} onClick={handleStatusPoll}
            title={selectedId ? "Poll selected soldier" : "Poll all soldiers"}>
            {polling ? "Pinging…" : selectedId ? "Ping soldier" : "Ping all"}
          </button>
        )}
        {boardAddress && fieldEventId && (
          <button style={{ ...styles.btn, color: colors.danger }} disabled={stopping} onClick={handleStop}>
            {stopping ? "Stopping…" : "Stop Event"}
          </button>
        )}
      </div>

      {eventStopped && (
        <div style={{
          padding: "10px 16px", background: colors.danger + "20",
          borderBottom: `1px solid ${colors.danger}44`, color: colors.danger,
          fontSize: "0.875rem", fontWeight: 500, textAlign: "center",
        }}>
          Field event ended — session is now read-only
        </div>
      )}

      {/* Split table view */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {soldiers.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center", color: colors.textMuted }}>
            No soldiers paired yet — go to Device Setup
          </div>
        ) : (
          <>
            <SoldierTable
              {...sharedProps}
              soldiers={assignedSoldiers}
              label={isHost ? "Assigned" : "My Assignments"}
            />
            <SoldierTable
              {...sharedProps}
              soldiers={otherSoldiers}
              label={assignedSoldiers.length > 0 ? "All Soldiers" : undefined}
            />
          </>
        )}
      </div>

      {!mapBridge.isNative && <WebMap height="260px" />}
    </div>
  );
}
